/**
 * AI Reports Dashboard.
 *
 * One screen, three read levels — the same report file shows
 * different depths depending on who opens it (CEO scans in 10s,
 * HOD reads in 30s, HR / Audit drills into proof records).
 *
 * Style rules (per spec):
 *   - Numbers first, words second
 *   - AI summary is exactly 1-2 lines, never paragraphs
 *   - Big tiles up top, traffic-light KPI strip, sparkline for
 *     hourly calls, flags in red
 *   - Manual notes field for the agent (locked after EOD submit)
 *
 * Backend: /api/scheduled-reports/*
 *
 * Role detection:
 *   - ceo / owner / account_owner / super_admin -> CEO view (stripped)
 *   - hod / manager / admin                     -> HOD view (full)
 *   - hr / hr_admin                             -> HR view (full + proof)
 *   - everyone else                             -> own report only
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  RefreshCw, Download, AlertTriangle, TrendingUp, TrendingDown,
  Minus, Phone, Users, Target, CheckCircle2, ChevronRight, Lock,
  Sparkles, Award, AlertCircle, Activity,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CEO_ROLES = new Set(['ceo', 'owner', 'account_owner', 'super_admin']);
const HOD_ROLES = new Set([
  'hod', 'hod_sales', 'hod_marketing', 'manager', 'admin',
  ...CEO_ROLES,
]);
const HR_ROLES = new Set(['hr', 'hr_admin', ...HOD_ROLES]);

const REPORT_TYPE_LABELS = {
  mid_day: 'Mid-Day',
  end_of_day: 'End-of-Day',
  live: 'Live',
};

// ---------- helpers ------------------------------------------------------

function trendIcon(arrow) {
  if (arrow === '↑') return <TrendingUp className="w-4 h-4 text-emerald-500" />;
  if (arrow === '↓') return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
}

function kpiColor(value, kind) {
  // Traffic-light bands so HOD can glance at the strip
  if (value == null) return 'text-slate-400';
  if (kind === 'sla' || kind === 'quality' || kind === 'quota') {
    if (value >= 85) return 'text-emerald-600';
    if (value >= 70) return 'text-amber-500';
    return 'text-red-500';
  }
  return 'text-slate-700';
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

// ---------- atoms --------------------------------------------------------

function MetricTile({ label, value, avg, trend, icon: Icon }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-3xl font-semibold text-slate-900">{value ?? 0}</span>
        {trendIcon(trend)}
      </div>
      {avg != null && (
        <div className="text-xs text-slate-400 mt-1">vs {avg} avg</div>
      )}
    </div>
  );
}

function KpiPill({ label, value, kind, suffix = '%' }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-50">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm font-semibold ${kpiColor(value, kind)}`}>
        {value == null ? '—' : `${value}${suffix}`}
      </span>
    </div>
  );
}

function Sparkline({ values }) {
  // Tiny inline bar chart — no chart lib needed for 24 ints
  const max = Math.max(1, ...values);
  return (
    <div className="flex items-end gap-[2px] h-10">
      {values.map((v, i) => (
        <div
          key={i}
          title={`${v} calls at ${i}:00`}
          className="w-2 bg-blue-400 rounded-sm"
          style={{ height: `${(v / max) * 100}%`, minHeight: v ? '2px' : '0' }}
        />
      ))}
    </div>
  );
}

function FlagItem({ flag }) {
  const high = flag.severity === 'high';
  return (
    <div className={`flex items-start gap-2 p-2 rounded ${high ? 'bg-red-50' : 'bg-amber-50'}`}>
      <AlertTriangle className={`w-4 h-4 mt-0.5 ${high ? 'text-red-500' : 'text-amber-500'}`} />
      <div className="text-sm text-slate-700">{flag.label}</div>
    </div>
  );
}

// ---------- sections -----------------------------------------------------

function ReportHeader({ report, agentName }) {
  if (!report) return null;
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">
          {agentName || 'Agent'}
          <span className="ml-2 text-sm font-normal text-slate-500">
            {REPORT_TYPE_LABELS[report.report_type] || report.report_type} Report
            {' — '}
            {report.report_date}
          </span>
        </h2>
        {report.submitted_to_hod_at && (
          <Badge variant="outline" className="mt-1 gap-1">
            <Lock className="w-3 h-3" /> Submitted to HOD + CEO
          </Badge>
        )}
      </div>
      {report.version > 1 && (
        <span className="text-xs text-slate-400">v{report.version}</span>
      )}
    </div>
  );
}

function AISummary({ line }) {
  if (!line) return null;
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 mb-4 flex items-start gap-2">
      <Sparkles className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
      <p className="text-sm text-slate-800 italic">{line}</p>
    </div>
  );
}

function TilesRow({ tiles }) {
  if (!tiles) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      <MetricTile
        label="Leads Today"
        value={tiles.leads_today?.value}
        avg={tiles.leads_today?.avg_28d}
        trend={tiles.leads_today?.trend}
        icon={Users}
      />
      <MetricTile
        label="Calls Made"
        value={tiles.calls_made?.value}
        avg={tiles.calls_made?.avg_28d}
        trend={tiles.calls_made?.trend}
        icon={Phone}
      />
      <MetricTile
        label="Missed Leads"
        value={tiles.missed_leads?.value}
        avg={tiles.missed_leads?.avg_28d}
        trend={tiles.missed_leads?.trend}
        icon={AlertCircle}
      />
      <MetricTile
        label="Closed Won"
        value={tiles.closed_won?.value}
        avg={tiles.closed_won?.avg_28d}
        trend={tiles.closed_won?.trend}
        icon={Target}
      />
    </div>
  );
}

function KpiStrip({ kpi }) {
  if (!kpi) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-4 p-3 rounded-lg border border-slate-200 bg-slate-50">
      <KpiPill label="SLA" value={kpi.sla_compliance_pct} kind="sla" />
      <KpiPill label="Quality" value={kpi.quality_score} kind="quality" suffix="/100" />
      <KpiPill label="Quota" value={kpi.quota_pct} kind="quota" />
      {kpi.productivity_pct != null && (
        <KpiPill label="Productivity" value={kpi.productivity_pct} kind="quality" />
      )}
    </div>
  );
}

function HourlyCalls({ buckets }) {
  if (!buckets || buckets.length === 0) return null;
  const total = buckets.reduce((a, b) => a + b, 0);
  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Phone className="w-4 h-4" /> Hourly Calls ({total} total)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Sparkline values={buckets} />
        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
          <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
        </div>
      </CardContent>
    </Card>
  );
}

function SiteVisitsAndMeetings({ siteVisits, meetings, closures }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Site Visits</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <div>Scheduled: <b>{siteVisits?.scheduled ?? 0}</b></div>
          <div>Completed: <b className="text-emerald-600">{siteVisits?.completed ?? 0}</b></div>
          <div>No-show: <b className="text-red-500">{siteVisits?.no_show ?? 0}</b></div>
          <div>Rescheduled: <b className="text-amber-500">{siteVisits?.rescheduled ?? 0}</b></div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Meetings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <div>Held: <b>{meetings?.held ?? 0}</b></div>
          <div>Upcoming: <b>{meetings?.upcoming ?? 0}</b></div>
          {meetings?.outcomes && Object.keys(meetings.outcomes).length > 0 && (
            <div className="pt-1 text-xs text-slate-500">
              {Object.entries(meetings.outcomes).map(([k, v]) => (
                <span key={k} className="mr-2">{k}: {v}</span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Closures</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <div>Won: <b className="text-emerald-600">{closures?.won ?? 0}</b></div>
          <div>Lost: <b className="text-red-500">{closures?.lost ?? 0}</b></div>
          <div>Bookings signed: <b>{closures?.bookings_signed ?? 0}</b></div>
          <div>Bookings pending: <b className="text-amber-500">{closures?.bookings_pending ?? 0}</b></div>
        </CardContent>
      </Card>
    </div>
  );
}

function Flags({ flags }) {
  if (!flags || flags.length === 0) return null;
  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500" /> Flags ({flags.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {flags.map((f, i) => <FlagItem key={i} flag={f} />)}
      </CardContent>
    </Card>
  );
}

function QualitySection({ quality, isHR }) {
  if (!quality) return null;
  const comp = quality.components || {};
  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Award className="w-4 h-4 text-blue-500" /> Quality Score
          <Badge variant="outline" className="ml-2">{quality.composite}/100</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3 text-xs">
          {Object.entries(comp).map(([k, v]) => (
            <div key={k} className="flex justify-between p-2 rounded bg-slate-50">
              <span className="text-slate-600">{k.replace(/_/g, ' ')}</span>
              <span className={`font-semibold ${kpiColor(v.score, 'quality')}`}>
                {v.score}
              </span>
            </div>
          ))}
        </div>
        {quality.strengths?.length > 0 && (
          <div className="text-xs mb-2">
            <span className="font-semibold text-emerald-700">Strengths:</span>{' '}
            {quality.strengths.join(' • ')}
          </div>
        )}
        {quality.improvements?.length > 0 && (
          <div className="text-xs">
            <span className="font-semibold text-amber-700">Improve:</span>{' '}
            {quality.improvements.join(' • ')}
          </div>
        )}
        {isHR && quality.flagged_interactions?.length > 0 && (
          <div className="mt-3 text-xs">
            <div className="font-semibold text-slate-700 mb-1">For review:</div>
            <ul className="space-y-1">
              {quality.flagged_interactions.map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <ChevronRight className="w-3 h-3 text-slate-400" />
                  <code className="text-[10px] bg-slate-100 px-1 rounded">{f.ref}</code>
                  <span>{f.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityVerification({ verification, isHR }) {
  if (!verification) return null;
  const pct = verification.verified_pct ?? 0;
  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Activity className="w-4 h-4 text-blue-500" /> Activity Verification
          <Badge variant="outline" className={kpiColor(pct, 'quality')}>{pct}%</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="text-xs space-y-1">
        <div>Total: <b>{verification.total}</b></div>
        <div className="text-emerald-700">Verified: <b>{verification.verified}</b></div>
        <div className="text-red-600">Unverifiable: <b>{verification.unverified}</b></div>
        {isHR && verification.unverifiable?.length > 0 && (
          <ul className="mt-2 space-y-1">
            {verification.unverifiable.slice(0, 5).map((u, i) => (
              <li key={i} className="flex items-center gap-2 text-slate-600">
                <ChevronRight className="w-3 h-3" />
                <span className="font-mono text-[10px]">{u.activity_type}</span>
                <span className="text-red-600">— {u.reason}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AgentNotes({ report, isOwner, onSave }) {
  const [val, setVal] = useState(report?.agent_notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setVal(report?.agent_notes || ''); }, [report?.id]);

  if (!report) return null;
  const locked = report.notes_locked || !isOwner;

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          Manual Notes
          {locked && <Badge variant="outline" className="gap-1"><Lock className="w-3 h-3" />Locked</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Textarea
          value={val}
          disabled={locked}
          onChange={(e) => setVal(e.target.value)}
          placeholder={isOwner ? 'Add any context you want the manager to see…' : 'Agent has not added notes.'}
          rows={3}
          className="text-sm"
        />
        {isOwner && !locked && (
          <Button
            size="sm"
            className="mt-2"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await onSave(val);
                toast.success('Notes saved');
              } catch (e) {
                toast.error('Save failed');
              } finally {
                setSaving(false);
              }
            }}
          >
            Save notes
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- agent list (HOD / CEO) --------------------------------------

function AgentReportList({ reports, onPick, selectedId }) {
  if (!reports || reports.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-slate-500">
          No reports yet for this window. Generation runs at 13:00 (mid-day) and 20:00 (end-of-day) local time.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Agents ({reports.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 max-h-[600px] overflow-y-auto">
        {reports.map((r) => {
          const s = r.structured || {};
          const flagN = (s.flags || []).length;
          const high = (s.flags || []).filter((f) => f.severity === 'high').length;
          return (
            <div
              key={r.id}
              onClick={() => onPick(r)}
              className={`p-2 rounded cursor-pointer hover:bg-slate-50 ${selectedId === r.id ? 'bg-blue-50' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{r.agent_name || r.agent_id?.slice(0, 8)}</span>
                {high > 0 && <Badge variant="destructive">{high}</Badge>}
              </div>
              <div className="text-[10px] text-slate-500 italic line-clamp-1">{r.summary_line}</div>
              {flagN > 0 && (
                <div className="text-[10px] text-amber-600 mt-0.5">{flagN} flag(s)</div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ---------- main --------------------------------------------------------

export default function AIReportsDashboard() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const isCEO = CEO_ROLES.has(role);
  const isHOD = HOD_ROLES.has(role);
  const isHR = HR_ROLES.has(role);

  const [reportDate, setReportDate] = useState(today());
  const [reportType, setReportType] = useState('end_of_day');
  const [report, setReport] = useState(null);
  const [reportList, setReportList] = useState([]);
  const [agentNames, setAgentNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);

  const token = sessionStorage.getItem('auth_token');
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // Hydrate agent names for the HOD list view
  const hydrateNames = useCallback(async (rows) => {
    const ids = [...new Set(rows.map((r) => r.agent_id).filter(Boolean))];
    if (ids.length === 0) return;
    try {
      const { data } = await axios.post(
        `${API}/api/users/bulk-by-ids`,
        { ids },
        auth,
      );
      const map = {};
      (data?.users || []).forEach((u) => { map[u.id] = u.full_name || u.email; });
      setAgentNames(map);
    } catch {
      /* graceful: list still renders by id */
    }
  }, [auth]);

  // Load the report set for the current filters
  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isHOD && !selectedReportId) {
        // Manager view: list every report in the window
        const { data } = await axios.get(
          `${API}/api/scheduled-reports/list`,
          {
            ...auth,
            params: {
              date_from: reportDate,
              date_to: reportDate,
              report_type: reportType,
            },
          },
        );
        const rows = data?.reports || [];
        await hydrateNames(rows);
        // Attach name onto row for the picker
        const withNames = rows.map((r) => ({
          ...r,
          agent_name: agentNames[r.agent_id] || r.agent_id?.slice(0, 8),
        }));
        setReportList(withNames);
        // Default to the first report with a high-severity flag, else first
        const top = withNames.find(
          (r) => (r.structured?.flags || []).some((f) => f.severity === 'high'),
        ) || withNames[0];
        setReport(top || null);
        setSelectedReportId(top?.id || null);
      } else {
        // Agent view (or HOD with a selection): one report
        const { data } = await axios.get(
          `${API}/api/scheduled-reports/today`,
          {
            ...auth,
            params: {
              agent_id: selectedReportId ? undefined : user?.id,
              report_type: reportType,
              report_date: reportDate,
            },
          },
        );
        setReport(data?.report || null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [
    isHOD, selectedReportId, reportDate, reportType, auth,
    user?.id, hydrateNames, agentNames,
  ]);

  useEffect(() => { load(); }, [reportDate, reportType, isHOD]);  // eslint-disable-line

  const saveNotes = async (text) => {
    if (!report?.id) return;
    await axios.post(
      `${API}/api/scheduled-reports/${report.id}/notes`,
      { note_text: text },
      auth,
    );
    setReport({ ...report, agent_notes: text });
  };

  const refresh = async () => {
    if (!report?.agent_id) return;
    try {
      const { data } = await axios.post(
        `${API}/api/scheduled-reports/generate`,
        {
          agent_id: report.agent_id,
          report_type: reportType,
          report_date: reportDate,
          force: true,
        },
        auth,
      );
      setReport(data?.report);
      toast.success('Report regenerated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Regeneration failed');
    }
  };

  const downloadBundle = async () => {
    try {
      const resp = await axios.get(
        `${API}/api/scheduled-reports/bulk-download`,
        {
          ...auth,
          params: {
            date_from: reportDate,
            date_to: reportDate,
            report_type: reportType,
            fmt: 'csv',
          },
          responseType: 'blob',
        },
      );
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `agent_reports_${reportDate}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const isOwner = report?.agent_id === user?.id;
  const structured = report?.structured || {};

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">AI Reports</h1>
          <p className="text-sm text-slate-500">
            {isCEO ? 'Scan in 10 seconds. ' : ''}
            Auto-generated Mid-Day (13:00) and End-of-Day (20:00) reports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
            className="w-40"
          />
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mid_day">Mid-Day</SelectItem>
              <SelectItem value="end_of_day">End-of-Day</SelectItem>
              <SelectItem value="live">Live</SelectItem>
            </SelectContent>
          </Select>
          {isHOD && (
            <Button variant="outline" size="sm" onClick={downloadBundle}>
              <Download className="w-4 h-4 mr-1" /> Bundle
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {loading && <Skeleton className="h-96 w-full" />}

      {!loading && (
        <div className={`grid ${isHOD ? 'grid-cols-1 lg:grid-cols-[280px_1fr] gap-4' : 'grid-cols-1'}`}>
          {isHOD && (
            <AgentReportList
              reports={reportList}
              selectedId={selectedReportId}
              onPick={(r) => { setReport(r); setSelectedReportId(r.id); }}
            />
          )}
          <div>
            {!report && (
              <Card>
                <CardContent className="py-12 text-center text-sm text-slate-500">
                  No report yet for this window. It will appear here after
                  the next scheduled generation (13:00 mid-day, 20:00 EOD).
                </CardContent>
              </Card>
            )}
            {report && (
              <>
                <ReportHeader
                  report={report}
                  agentName={agentNames[report.agent_id] || (isOwner ? user?.full_name : null)}
                />
                <AISummary line={report.summary_line} />

                {/* CEO read level stops here — tiles + flags only */}
                <TilesRow tiles={structured.tiles} />
                {isCEO && !isHOD && (
                  <Flags flags={(structured.flags || []).filter((f) => f.severity === 'high')} />
                )}

                {/* HOD / HR / agent read level: full payload */}
                {(isHOD || isOwner) && (
                  <>
                    <KpiStrip kpi={structured.kpi_strip} />
                    <HourlyCalls buckets={structured.hourly_calls} />
                    <SiteVisitsAndMeetings
                      siteVisits={structured.site_visits}
                      meetings={structured.meetings}
                      closures={structured.closures}
                    />
                    <Flags flags={structured.flags} />
                    <QualitySection quality={structured.quality} isHR={isHR} />
                    <ActivityVerification
                      verification={structured.activity_verification}
                      isHR={isHR}
                    />
                    <AgentNotes
                      report={report}
                      isOwner={isOwner}
                      onSave={saveNotes}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
